import { useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { LogOut, UserCog } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * 계정 메뉴. **Clerk의 `<UserButton />` 대신 직접 그린다.**
 *
 * 왜: `UserButton`을 붙이면 Clerk이 UI 컴포넌트 묶음(`ui-common`)을 받아온다.
 * 느린 휴대폰(Fast 3G + CPU 4배)에서 그 파일 하나가 **1.2초**였다(실측). 로그인된
 * 사용자가 여는 모든 화면에 그 값이 붙는데, 정작 쓰는 건 아바타와 메뉴 두 줄이다.
 * `useClerk`·`useUser`는 UI가 아니라 코어에 있어서 추가 다운로드가 없다.
 *
 * 프로필·비밀번호 같은 **계정 자체 관리**는 Clerk 계정 포털로 보낸다 — 우리가
 * 다시 만들 이유가 없고, 거기가 정본이다.
 */
export function AccountMenu() {
  const { signOut, user } = useClerk();
  const { user: loaded } = useUser();
  const [busy, setBusy] = useState(false);

  const who = loaded ?? user;
  const label = who?.primaryEmailAddress?.emailAddress ?? who?.fullName ?? "내 계정";
  const image = who?.imageUrl;
  // 이미지가 없을 때 쓸 글자. 이메일이면 @ 앞 첫 글자다.
  const initial = (who?.firstName ?? label).trim().charAt(0).toUpperCase() || "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        aria-label="계정 메뉴"
      >
        {image ? (
          <img src={image} alt="" className="size-full object-cover" />
        ) : (
          <span aria-hidden>{initial}</span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuLabel className="truncate font-normal text-muted-foreground">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href="https://accounts.gotravel.day/user" target="_blank" rel="noreferrer">
            <UserCog aria-hidden />
            계정 관리
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={busy}
          onSelect={(e) => {
            // 기본 닫기를 막는다 — 로그아웃이 끝나기 전에 메뉴가 사라지면
            // 사용자는 눌린 건지 알 수 없다.
            e.preventDefault();
            setBusy(true);
            void signOut({ redirectUrl: "/" });
          }}
        >
          <LogOut aria-hidden />
          {busy ? "로그아웃 중…" : "로그아웃"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
